type SourceImage = Record<string, unknown> | null | undefined;

function normalizeNewsTitle(title: string | null | undefined) {
  return typeof title === "string" ? title.trim() : "";
}

export function getNewsDisplayLabel(input: {
  sourcePlatform?: string | null;
  sourceAuthorName?: string | null;
  sourceAuthorHandle?: string | null;
  title?: string | null | undefined;
  fallback?: string;
}) {
  const normalizedTitle = normalizeNewsTitle(input.title);
  if (normalizedTitle.length > 0) {
    return normalizedTitle;
  }

  if ((input.sourcePlatform || "").toLowerCase() === "x") {
    const displayName = input.sourceAuthorName?.trim();
    if (displayName) {
      return displayName;
    }
    return input.sourceAuthorHandle ? `@${input.sourceAuthorHandle} 的 X Thread` : "X Thread";
  }

  return input.fallback || "新闻";
}

function getImageUrlFromValue(value: unknown): string | null {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }

  if (value && typeof value === "object") {
    const maybeUrl = (value as Record<string, unknown>).url;
    if (typeof maybeUrl === "string" && maybeUrl.length > 0) {
      return maybeUrl;
    }

    const maybeSrc = (value as Record<string, unknown>).src;
    if (typeof maybeSrc === "string" && maybeSrc.length > 0) {
      return maybeSrc;
    }
  }

  return null;
}

export function pickPreferredNewsImage(sourceImage: SourceImage): string | null {
  if (!sourceImage || typeof sourceImage !== "object") {
    return null;
  }

  const imageRecord = sourceImage as Record<string, unknown>;
  const candidateKeys = ["ar16X9", "ar3X1", "ar2X1", "ar1X1", "image", "thumbnail"];

  for (const key of candidateKeys) {
    const picked = getImageUrlFromValue(imageRecord[key]);
    if (picked) {
      return picked;
    }
  }

  for (const value of Object.values(imageRecord)) {
    const picked = getImageUrlFromValue(value);
    if (picked) {
      return picked;
    }
  }

  return null;
}

export function extractFirstImageFromMarkdown(markdown: string | null | undefined): string | null {
  if (!markdown || markdown.length === 0) {
    return null;
  }

  const markdownImage = markdown.match(/!\[[^\]]*]\(([^)\s]+)(?:\s+"[^"]*")?\)/);
  if (markdownImage?.[1]) {
    return markdownImage[1];
  }

  const htmlImage = markdown.match(/<img[^>]+src=["']([^"']+)["']/i);
  if (htmlImage?.[1]) {
    return htmlImage[1];
  }

  return null;
}

export function resolveNewsCoverImage(input: {
  sourceImage?: SourceImage;
  translatedBody?: string | null;
  sourceBody?: string | null;
}) {
  return (
    pickPreferredNewsImage(input.sourceImage) ||
    extractFirstImageFromMarkdown(input.translatedBody) ||
    extractFirstImageFromMarkdown(input.sourceBody)
  );
}

function isEaDomain(hostname: string) {
  return hostname === "ea.com" || hostname.endsWith(".ea.com");
}

function isXMediaDomain(hostname: string) {
  return (
    hostname === "pbs.twimg.com" ||
    hostname === "video.twimg.com" ||
    hostname === "ton.twimg.com" ||
    hostname === "pbs.x.com" ||
    hostname === "video.x.com"
  );
}

function getPathnameFromUrl(url: string) {
  try {
    return new URL(url).pathname.toLowerCase();
  } catch {
    return url.split("?")[0]?.toLowerCase() || "";
  }
}

export function isVideoAssetUrl(url: string | null | undefined) {
  if (!url) {
    return false;
  }
  const pathname = getPathnameFromUrl(url);
  return pathname.endsWith(".mp4") || pathname.endsWith(".webm") || pathname.endsWith(".mov");
}

export function toNewsAssetUrlForRender(
  imageUrl: string | null | undefined,
  options?: { referer?: string | null },
) {
  if (!imageUrl || imageUrl.length === 0) {
    return null;
  }

  if (imageUrl.startsWith("data:")) {
    return imageUrl;
  }

  try {
    const absolute = options?.referer ? new URL(imageUrl, options.referer) : new URL(imageUrl);
    if (isEaDomain(absolute.hostname) || isXMediaDomain(absolute.hostname)) {
      const search = new URLSearchParams({ url: absolute.toString() });
      if (options?.referer) {
        search.set("referer", options.referer);
      }
      return `/api/apex-news-image?${search.toString()}`;
    }
    return absolute.toString();
  } catch {
    return imageUrl;
  }
}

export const toNewsImageUrlForRender = toNewsAssetUrlForRender;

const EA_NEWS_TRAILING_BOILERPLATE_SIGNATURES = [
  "apex-legends/apex-legends/buy",
  "apex-legends/newsletter",
  "ea.com/service-updates",
  "twitter.com/playapex",
  "instagram.com/playapex",
  "tiktok.com/@playapex",
  "youtube.com/playapex",
  "answers.ea.com/t5/apex-legends/ct-p/apex-legends-en",
  "applicable platform account and platform subscription",
  "not available in all territories",
];

function stripTrailingEaNewsBoilerplate(markdown: string) {
  const lines = markdown.split("\n");

  for (let index = lines.length - 1; index >= 0; index -= 1) {
    if (lines[index]?.trim() !== "---") {
      continue;
    }

    const trailingSection = lines.slice(index).join("\n").toLowerCase();
    const matchedSignatureCount = EA_NEWS_TRAILING_BOILERPLATE_SIGNATURES.filter((signature) =>
      trailingSection.includes(signature),
    ).length;
    const hasBuyLink = trailingSection.includes("apex-legends/apex-legends/buy");

    if (hasBuyLink && matchedSignatureCount >= 3) {
      return lines.slice(0, index).join("\n").trimEnd();
    }
  }

  return markdown;
}

export function parseNewsMarkdownForRender(markdown: string | null | undefined) {
  if (!markdown) {
    return {
      markdown: "",
      headingAnchorIdByLine: new Map<number, string>(),
    };
  }

  const strippedMarkdown = stripTrailingEaNewsBoilerplate(markdown);
  const tableWithHeadBodyPattern =
    /\[table]\(\s*(<thead[\s\S]*?<\/thead>)\s*\)\(\s*(<tbody[\s\S]*?<\/tbody>)\s*\)/gi;
  const tableOnlyPattern = /\[table]\(\s*(<table[\s\S]*?<\/table>)\s*\)/gi;
  const normalizeInlineStrongTag = (content: string) =>
    content.replace(/\*\*([\s\S]+?)\*\*/g, "<strong>$1</strong>");
  const wrapAsBlock = (content: string) => `\n\n${content}\n\n`;
  const normalizedTableMarkdown = strippedMarkdown
    .replace(tableWithHeadBodyPattern, (_, thead: string, tbody: string) => {
      return wrapAsBlock(
        `<table>${normalizeInlineStrongTag(thead)}${normalizeInlineStrongTag(tbody)}</table>`,
      );
    })
    .replace(tableOnlyPattern, (_, table: string) => wrapAsBlock(normalizeInlineStrongTag(table)));

  const headingAnchorIdByLine = new Map<number, string>();
  const headingAnchorPattern = /\s*(?:\*\*\{#([^}\n]+)}\*\*|\{#([^}\n]+)})\s*$/;
  const lines = normalizedTableMarkdown.split("\n");

  const normalizedLines = lines.map((line, index) => {
    const headingMatch = line.match(/^(\s{0,3}#{1,6}\s+)(.*)$/);
    if (!headingMatch) {
      return line;
    }

    const headingPrefix = headingMatch[1];
    const headingContent = headingMatch[2] ?? "";
    const anchorMatch = headingContent.match(headingAnchorPattern);
    if (!anchorMatch) {
      return line;
    }

    const anchorId = (anchorMatch[1] || anchorMatch[2] || "").trim();
    if (anchorId.length > 0) {
      headingAnchorIdByLine.set(index + 1, anchorId);
    }

    const normalizedHeadingContent = headingContent.replace(headingAnchorPattern, "");
    return `${headingPrefix}${normalizedHeadingContent}`;
  });

  return {
    markdown: normalizedLines.join("\n"),
    headingAnchorIdByLine,
  };
}

export function sanitizeNewsMarkdownForRender(markdown: string | null | undefined) {
  return parseNewsMarkdownForRender(markdown).markdown;
}
